import * as fp from 'lodash/fp';
import ChangeEpisodicItem from './change-episodic-items/change-episodic-item.entity';
import CharScene from './char-scenes/char-scene.entity';
import { DEPARTMENTS } from './enums/enums';
import PropSceneCharScene from './prop-scene-char-scenes/prop-scene-char-scene.entity';
import SetPieceSet from './set-piece-sets/set-piece-set.entity';

export const TextMax = 65535;

/**
 * Mimics bluebirds Promise.props
 * awaits all properties of an object, and returns an object with resolved/rejected values with corresponding keys
 */
export async function promiseAllProps(obj: Object) {
	var keys = [];
	var values = [];
	for (var key in obj) {
		if (obj.hasOwnProperty(key)) {
			keys.push(key);
			values.push(obj[key]);
		}
	}

	const newObj = {};
	const results = await Promise.all(values);
	results.forEach((res, i) => {
		newObj[keys[i]] = res;
	});

	return newObj;
}

export function notNullOrEmptyOrUndefined(str: any) {
	// tslint:disable-line:no-any
	return str !== null && str !== '' && str !== undefined;
}

export const compareCharScenes: (
	csA: CharScene,
	csB: CharScene
) => boolean = fp.isEqualWith((cs1, cs2) =>
	fp.isEqual(
		fp.pick(['char_id', 'scene_id'], cs1),
		fp.pick(['char_id', 'scene_id'], cs2)
	)
);

export const comparePropSceneCharScenes: (
	pscsA: PropSceneCharScene,
	pscsB: PropSceneCharScene
) => boolean = fp.isEqualWith((pscs1, pscs2) =>
	fp.isEqual(
		fp.pick(['prop_scene_id', 'char_scene_id'], pscs1),
		fp.pick(['prop_scene_id', 'char_scene_id'], pscs2)
	)
);

export const compareSetPieceSets: (
	pscsA: SetPieceSet,
	pscsB: SetPieceSet
) => boolean = fp.isEqualWith((pscs1, pscs2) =>
	fp.isEqual(
		fp.pick(['set_piece_id', 'set_id'], pscs1),
		fp.pick(['set_piece_id', 'set_id'], pscs2)
	)
);

// TODO: we can make this fancier in the future following these rules for pluralization: https://www.grammarly.com/blog/plural-nouns/
export const pluralize = (word: string, quantity: number) =>
	quantity === 1 ? word : `${word}s`;

export const toListPhrase = (terms: string[]) =>
	terms.length === 0
		? ''
		: terms.length === 1
		? terms[0]
		: [
				terms.slice(0, terms.length - 1).join(', '),
				terms[terms.length - 1],
		  ].join(' and ');

export const stringifyDepartment = (dept: DEPARTMENTS): string => {
	switch (dept) {
		case DEPARTMENTS.CM:
			return 'costume';
		case DEPARTMENTS.HA:
			return 'hair';
		case DEPARTMENTS.MU:
			return 'makeup';
		case DEPARTMENTS.PR:
			return 'props';
		case DEPARTMENTS.SET:
			return 'set dec';
		default:
			return '';
	}
};

export const NON_WORD_CHAR_REGEX = `[\\s\\/\\.,\\+\\-\\[\\]\\{\\}"]`;
export const TRIM_REGEX = new RegExp(
	`(^${NON_WORD_CHAR_REGEX}+)|(${NON_WORD_CHAR_REGEX}+$)`,
	'g'
);
export const NON_WORD_CHAR_SEQ = new RegExp(`${NON_WORD_CHAR_REGEX}+`);
export const NON_WORD_CHAR_ENDING = new RegExp(`${NON_WORD_CHAR_REGEX}$`);

export const tokenize = (str: string) =>
	str.toLowerCase().replace(TRIM_REGEX, '').split(NON_WORD_CHAR_SEQ);
export const endsWithNonWordChar = (str: string) =>
	!!str.match(NON_WORD_CHAR_ENDING);

/**
 * @param changeEpisodicItems - should be sorted by custom_sort_id then name
 * @param epItemId - item id of ChangeEpItem to reorder
 * @param dest - where it should go
 * @returns - array of ChangeEpItems with new custom_sort_index values
 */
export function reorderChangeEpisodicItems(
	changeEpisodicItems: ChangeEpisodicItem[],
	epItemId: number,
	dest: number
): ChangeEpisodicItem[] {
	const changeEpItemToReorder = changeEpisodicItems.find(
		({ episodic_item_id }) => episodic_item_id === epItemId
	);
	const [changeEpItemsWithSort, changeEpItemsWithoutSort] = fp.partition(
		({ custom_sort_index }) =>
			typeof custom_sort_index !== 'undefined' && custom_sort_index !== null,
		changeEpisodicItems
	);
	let maxIndex: number =
		fp.pipe(fp.map('custom_sort_index'), fp.max)(changeEpItemsWithSort) || 0;

	let changeEpItemsToUpdate = changeEpItemsWithoutSort.map((cei, index) => ({
		...cei,
		custom_sort_index: 100 * (index + 1) + maxIndex,
	}));

	const allChangeEpItemsWithIndices = [
		...fp.sortBy('custom_sort_index', changeEpItemsWithSort),
		...changeEpItemsToUpdate,
	].filter(({ id }) => id !== changeEpItemToReorder.id);

	if (allChangeEpItemsWithIndices.length) {
		maxIndex =
			allChangeEpItemsWithIndices[allChangeEpItemsWithIndices.length - 1]
				.custom_sort_index;
	}

	let newSortIndex: number;
	if (dest === 0) {
		newSortIndex = allChangeEpItemsWithIndices[0].custom_sort_index / 2;
	} else if (dest === changeEpisodicItems.length - 1) {
		newSortIndex = maxIndex + 100;
	} else {
		newSortIndex =
			(allChangeEpItemsWithIndices[dest - 1].custom_sort_index +
				allChangeEpItemsWithIndices[dest].custom_sort_index) /
			2;
	}

	return fp
		.uniqBy('id', [
			{ ...changeEpItemToReorder, custom_sort_index: newSortIndex },
			...changeEpItemsToUpdate,
		])
		.map((cei) => new ChangeEpisodicItem(cei));
}
